// Intent: owner testing — a user tried to work out his monthly budget in his
// head, took ages and gave up ("nie wiedział jaki budżet wpisać"). This is a
// tiny guided calculator: it asks about the common Polish monthly costs (czynsz,
// jedzenie, rachunki, transport, raty, subskrypcje, inwestycje…), sums them live
// and hands the total back as a suggested budget. No storage of its own — it
// only produces a number the caller applies. Ionicons only, no emoji.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { TextField } from '@/components/ui/text-field';
import { colors, spacing } from '@/theme/tokens';
import { formatPln } from '@/utils/format';

type FieldKey = 'rent' | 'food' | 'bills' | 'transport' | 'loans' | 'subs' | 'invest' | 'other';

const FIELDS: { key: FieldKey; labelKey: string; icon: keyof typeof Ionicons.glyphMap }[] = [
  { key: 'rent', labelKey: 'budgetCalc.rent', icon: 'home-outline' },
  { key: 'food', labelKey: 'budgetCalc.food', icon: 'restaurant-outline' },
  { key: 'bills', labelKey: 'budgetCalc.bills', icon: 'receipt-outline' },
  { key: 'transport', labelKey: 'budgetCalc.transport', icon: 'car-outline' },
  { key: 'loans', labelKey: 'budgetCalc.loans', icon: 'card-outline' },
  { key: 'subs', labelKey: 'budgetCalc.subs', icon: 'tv-outline' },
  { key: 'invest', labelKey: 'budgetCalc.invest', icon: 'trending-up-outline' },
  { key: 'other', labelKey: 'budgetCalc.other', icon: 'ellipsis-horizontal' },
];

// Categories that behave like a monthly bill with a due day — only these get
// the optional "dzień płatności" follow-up (food/transport are variable spend).
const FIXED_KEYS: FieldKey[] = ['rent', 'bills', 'loans', 'subs'];

/** A fixed cost the caller may create from an estimator row (day was given). */
export interface FixedCostDraft {
  name: string;
  amountGr: number;
  dueDay: number;
}

function toGr(raw: string): number {
  const digits = raw.replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) * 100 : 0;
}

/**
 * Guided monthly-budget calculator. Calls onApply with the summed total (gr).
 * INTENT (owner, onboarding 1.4.0): a filled bill-like row (czynsz…) is a
 * fixed cost the user already told us about — with collectFixedDays the row
 * asks "kiedy płacisz?" and onApply hands back FixedCostDrafts, so the caller
 * can save them as opłaty stałe with a reminder instead of losing the info.
 * Day left empty = the amount only feeds the budget sum, nothing else happens.
 */
export function BudgetEstimator({
  onApply,
  collectFixedDays = false,
}: {
  onApply: (totalGr: number, fixedDrafts?: FixedCostDraft[]) => void;
  collectFixedDays?: boolean;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<FieldKey, string>>({
    rent: '', food: '', bills: '', transport: '', loans: '', subs: '', invest: '', other: '',
  });
  const [days, setDays] = useState<Record<FieldKey, string>>({
    rent: '', food: '', bills: '', transport: '', loans: '', subs: '', invest: '', other: '',
  });
  const totalGr = FIELDS.reduce((sum, f) => sum + toGr(values[f.key]), 0);

  const fixedDrafts: FixedCostDraft[] = collectFixedDays
    ? FIXED_KEYS.flatMap((key) => {
        const amountGr = toGr(values[key]);
        const day = parseInt(days[key].replace(/[^\d]/g, ''), 10);
        if (amountGr <= 0 || !Number.isInteger(day) || day < 1 || day > 28) return [];
        const field = FIELDS.find((f) => f.key === key);
        return [{ name: t(field?.labelKey ?? 'budgetCalc.other'), amountGr, dueDay: day }];
      })
    : [];

  return (
    <Card style={{ gap: spacing.sm, backgroundColor: colors.infoBg }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
        <Ionicons name="calculator-outline" size={20} color={colors.primary} />
        <AppText variant="labelLg" color={colors.navy} style={{ flex: 1 }}>
          {t('budgetCalc.title')}
        </AppText>
      </View>
      <AppText variant="bodyMd" color={colors.textSecondary}>
        {t('budgetCalc.subtitle')}
      </AppText>
      {FIELDS.map((f) => {
        const askDay = collectFixedDays && FIXED_KEYS.includes(f.key) && toGr(values[f.key]) > 0;
        return (
          <View key={f.key} style={{ gap: spacing.xs }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <Ionicons name={f.icon} size={20} color={colors.navy} />
              <View style={{ flex: 1 }}>
                <TextField
                  label={t(f.labelKey)}
                  placeholder="0"
                  keyboardType="numeric"
                  value={values[f.key]}
                  onChangeText={(v) => setValues((s) => ({ ...s, [f.key]: v.replace(/[^\d]/g, '') }))}
                />
              </View>
            </View>
            {askDay && (
              <View style={{ marginLeft: 20 + spacing.sm, gap: 2 }}>
                <TextField
                  label={t('budgetCalc.dayLabel')}
                  placeholder="np. 10"
                  keyboardType="numeric"
                  value={days[f.key]}
                  onChangeText={(v) => setDays((s) => ({ ...s, [f.key]: v.replace(/[^\d]/g, '').slice(0, 2) }))}
                />
                <AppText variant="labelMd" color={colors.textSecondary}>
                  {t('budgetCalc.dayHint')}
                </AppText>
              </View>
            )}
          </View>
        );
      })}
      <View style={{ flexDirection: 'row', alignItems: 'center', paddingTop: spacing.xs }}>
        <AppText variant="labelLg" style={{ flex: 1 }}>
          {t('budgetCalc.sum')}
        </AppText>
        <AppText variant="headlineSm" color={colors.navy}>
          {formatPln(totalGr)}
        </AppText>
      </View>
      <PrimaryButton
        label={t('budgetCalc.apply', { amount: formatPln(totalGr) })}
        disabled={totalGr <= 0}
        onPress={() => onApply(totalGr, fixedDrafts)}
      />
    </Card>
  );
}
